import React, { useEffect, useRef, useState } from "react";
import "./CrosswordGame.css";
import {
    askHolmes,
    bankFor,
    bankUsed,
    CASEBOOK_CASES,
    caseWorth,
    CasebookState,
    CaseState,
    checkCase,
    CrosswordPuzzle,
    CrosswordRank,
    CrosswordResult,
    enterNumber,
    GLASSES_PER_CASE,
    MAX_GLASSES,
    nextCase,
    RANK_RULES,
    resultOf,
    startCasebook,
} from "./crosswordGame.ts";
import { useCrosswordText } from "./crosswordText.ts";
import { Lens, Plate, Silhouette } from "./CrosswordArt.tsx";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

/*
 * One casebook of the Baker Street Math Crossword (ADR 0134): five cases at one rank.
 * The rules are crosswordGame.ts; this is the screen: what is selected, the message
 * line, focus and the live region.
 *
 * Built in the first render from the engine's own start, so the first case is on the
 * page when it paints and nothing moves when it arrives (ADR 0067). The engine draws
 * from the random source in the handlers here, never inside a state updater (ADR 0053
 * §8): the state lives in a ref with a snapshot published to React.
 *
 * Every place the page can change has its space held before it changes: the lattice is
 * the same size for a rank whatever the case, the title and brief have their room, and
 * the message line keeps room for the longest hint. Only the lens ring and the fog move,
 * and they are transforms and opacity.
 *
 * Input. Each blank is a text box that opens the numeric keypad on a phone and takes up
 * to three digits. The arrow keys move to the nearest blank in that direction; Tab keeps
 * reading order. With the bank shown (Constable and Inspector) a bank number goes into the
 * selected blank and is struck off; a Detective has no bank. The case closes by itself
 * when the last blank is right: there is no submit.
 *
 * A live region reads each case's title and brief, each hint, each check and the close of
 * a case. The visible message line shows the same without being a second live region.
 */

interface CrosswordGameRunProps {
    rank: CrosswordRank;
    /** Called once, when the fifth case has been closed and left. */
    onFinish(result: CrosswordResult): void;
}

/** The first blank with nothing in it after `from`, reading order, wrapping; `from` itself if none is empty. */
function nextOpenBlank(c: CaseState, from: number | null): number | null {
    const blanks = c.puzzle.blanks;
    const start = from === null ? -1 : blanks.indexOf(from);
    for (let step = 1; step <= blanks.length; step++) {
        const cell = blanks[(start + step + blanks.length) % blanks.length];
        if (c.entries[cell] === null) {
            return cell;
        }
    }
    return from ?? blanks[0] ?? null;
}

/** The blank nearest `from` in the direction of an arrow key, or null if there is none that way. */
function blankInDirection(puzzle: CrosswordPuzzle, from: number, key: string): number | null {
    const here = puzzle.cells[from];
    let best: number | null = null;
    let bestScore = Infinity;
    for (const cell of puzzle.blanks) {
        if (cell === from) {
            continue;
        }
        const there = puzzle.cells[cell];
        const dRow = there.row - here.row;
        const dCol = there.col - here.col;
        const along = key === "ArrowRight" ? dCol : key === "ArrowLeft" ? -dCol : key === "ArrowDown" ? dRow : -dRow;
        const across = key === "ArrowRight" || key === "ArrowLeft" ? Math.abs(dRow) : Math.abs(dCol);
        if (along <= 0) {
            continue;
        }
        // The nearest in the direction of travel, and of those the one most nearly in line.
        const score = across * 100 + along;
        if (score < bestScore) {
            bestScore = score;
            best = cell;
        }
    }
    return best;
}

export function CrosswordGameRun({ rank, onFinish }: CrosswordGameRunProps) {
    const t = useTranslation();
    const text = useCrosswordText();

    const stateRef = useRef<CasebookState | null>(null);
    if (stateRef.current === null) {
        stateRef.current = startCasebook(rank, getRandomInt);
    }
    const [book, setBook] = useState<CasebookState>(stateRef.current);
    const [selected, setSelected] = useState<number | null>(() => stateRef.current!.current.puzzle.blanks[0] ?? null);
    const [hinted, setHinted] = useState<number | null>(null);
    const [message, setMessage] = useState("");
    const [announce, setAnnounce] = useState("");
    const reported = useRef(false);
    const nextRef = useRef<HTMLButtonElement>(null);
    const inputs = useRef(new Map<number, HTMLInputElement>());

    const rules = RANK_RULES[rank];
    const current = book.current;
    const puzzle = current.puzzle;
    const solved = book.phase === "solved";
    const over = book.phase === "over";
    const textIndex = book.textOrder[book.caseNumber - 1];
    const lastCase = book.caseNumber >= CASEBOOK_CASES;
    const worth = caseWorth(current);
    const bank = rules.bank ? bankFor(puzzle) : [];
    const used = rules.bank ? bankUsed(puzzle, current.entries) : [];

    function arrival(state: CasebookState): string {
        const index = state.textOrder[state.caseNumber - 1];
        return format(t("flashCards.crosswordArriveTemplate"), {
            n: state.caseNumber,
            title: text.caseTitle(index),
            brief: text.caseBrief(index),
        });
    }

    // The first case is read out once the page is up: a live region only speaks a change.
    useEffect(() => {
        setAnnounce(arrival(stateRef.current!));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (over && !reported.current) {
            reported.current = true;
            onFinish(resultOf(stateRef.current!));
        }
    }, [over, onFinish]);

    // Once a case is closed, the button that opens the next is where the focus is, so Enter and Space both go on.
    useEffect(() => {
        if (solved) {
            nextRef.current?.focus({ preventScroll: true });
        }
    }, [solved, book.caseNumber]);

    function publish(next: CasebookState) {
        stateRef.current = next;
        setBook(next);
    }

    function say(line: string) {
        setMessage(line);
        setAnnounce(line);
    }

    function closedLine(state: CasebookState): string {
        return format(t("flashCards.crosswordSolvedTemplate"), { n: caseWorth(state.current), max: GLASSES_PER_CASE });
    }

    function fill(cell: number, value: number | null) {
        const before = stateRef.current!;
        if (before.phase !== "playing") {
            return;
        }
        const next = enterNumber(before, cell, value);
        if (next === before) {
            return;
        }
        publish(next);
        setHinted(null);
        if (next.phase === "solved") {
            say(closedLine(next));
        } else if (message !== "") {
            setMessage("");
        }
    }

    function onType(cell: number, raw: string) {
        const digits = raw.replace(/\D/g, "").slice(0, 3);
        fill(cell, digits === "" ? null : parseInt(digits, 10));
    }

    function placeFromBank(value: number) {
        const state = stateRef.current!;
        if (state.phase !== "playing") {
            return;
        }
        const target =
            selected !== null && !state.current.helped[selected] && state.current.puzzle.blanks.includes(selected)
                ? selected
                : nextOpenBlank(state.current, null);
        if (target === null) {
            return;
        }
        fill(target, value);
        const after = stateRef.current!;
        if (after.phase === "playing") {
            setSelected(nextOpenBlank(after.current, target));
        }
    }

    function check() {
        const before = stateRef.current!;
        if (before.phase !== "playing") {
            return;
        }
        const result = checkCase(before);
        publish(result.state);
        setHinted(null);
        if (result.wrongCount > 0) {
            say(
                result.wrongCount === 1
                    ? t("flashCards.crosswordCheckWrongOne")
                    : format(t("flashCards.crosswordCheckWrongMany"), { n: result.wrongCount })
            );
        } else if (result.filled > 0) {
            say(t("flashCards.crosswordCheckRight"));
        } else {
            say(t("flashCards.crosswordCheckEmpty"));
        }
    }

    function ask() {
        const before = stateRef.current!;
        if (before.phase !== "playing") {
            return;
        }
        const result = askHolmes(before, selected);
        if (!result.deduction) {
            return;
        }
        publish(result.state);
        setHinted(result.deduction.line);
        const line = text.hintText(before.current.puzzle, result.deduction);
        say(result.state.phase === "solved" ? `${line} ${closedLine(result.state)}` : line);
        if (result.state.phase === "playing" && result.deduction.cell === selected) {
            setSelected(nextOpenBlank(result.state.current, selected));
        }
    }

    function next() {
        const state = stateRef.current!;
        if (state.phase !== "solved") {
            return;
        }
        const upcoming = nextCase(state, getRandomInt);
        publish(upcoming);
        if (upcoming.phase === "over") {
            return;
        }
        setSelected(upcoming.current.puzzle.blanks[0] ?? null);
        setHinted(null);
        setMessage("");
        setAnnounce(arrival(upcoming));
    }

    function onKeyDown(cell: number, event: React.KeyboardEvent<HTMLInputElement>) {
        if (!event.key.startsWith("Arrow") || event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }
        event.preventDefault();
        const target = blankInDirection(puzzle, cell, event.key);
        if (target !== null) {
            inputs.current.get(target)?.focus();
        }
    }

    /** What is drawn at a place on the lattice. */
    function place(row: number, col: number) {
        const key = `${row},${col}`;
        const evenRow = row % 2 === 0;
        const evenCol = col % 2 === 0;
        if (evenRow && evenCol) {
            const cell = puzzle.cells.findIndex((c) => c.row === row && c.col === col);
            return cell >= 0 ? square(cell) : <span key={key} />;
        }
        if (evenRow === evenCol) {
            return <span key={key} />;
        }
        // An operator or an equals sign sits between two numbers of one line.
        const line = puzzle.lines.find((l) =>
            evenRow ? l.direction === "across" && l.number === row / 2 + 1 : l.direction === "down" && l.number === col / 2 + 1
        );
        const slot = ((evenRow ? col : row) - 1) / 2;
        if (!line || slot >= line.cells.length - 1) {
            return <span key={key} />;
        }
        return (
            <span key={key} className="cwSign" aria-hidden="true">
                {slot < line.ops.length ? line.ops[slot] : "="}
            </span>
        );
    }

    // A function that returns elements, not a component: a component made in here would be a new type
    // every render, and a box being typed in would be torn down and lose its focus.
    function square(cell: number) {
        const value = current.entries[cell];
        const blank = puzzle.blanks.includes(cell);
        const tags = puzzle.lines
            .filter((line) => line.cells[0] === cell)
            .map((line) => text.lineTag(line))
            .join(" ");
        const classes = ["cwNum", blank ? "cwNum--blank" : "cwNum--given"];
        if (blank && selected === cell && !solved) {
            classes.push("cwNum--selected");
        }
        if (current.marked.includes(cell)) {
            classes.push("cwNum--wrong");
        }
        if (current.helped[cell]) {
            classes.push("cwNum--helped");
        }
        if (hinted !== null && puzzle.lines[hinted].cells.includes(cell)) {
            classes.push("cwNum--hinted");
        }
        if (blank && solved) {
            classes.push("cwNum--closed");
        }
        return (
            <div key={cell} className={classes.join(" ")}>
                {tags && (
                    <span className="cwTag" aria-hidden="true">
                        {tags}
                    </span>
                )}
                {blank ? (
                    <input
                        ref={(node) => {
                            if (node) {
                                inputs.current.set(cell, node);
                            } else {
                                inputs.current.delete(cell);
                            }
                        }}
                        className="cwInput"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={3}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        aria-label={text.cellLabel(puzzle, cell)}
                        aria-invalid={current.marked.includes(cell) ? true : undefined}
                        readOnly={solved || current.helped[cell]}
                        value={value === null ? "" : String(value)}
                        onChange={(event) => onType(cell, event.target.value)}
                        onFocus={() => setSelected(cell)}
                        onKeyDown={(event) => onKeyDown(cell, event)}
                    />
                ) : (
                    <span className="cwGiven">{value}</span>
                )}
                {current.marked.includes(cell) && (
                    <span className="cwBadge" aria-hidden="true">
                        ✗
                    </span>
                )}
            </div>
        );
    }

    const rows = Array.from({ length: puzzle.size }, (_, row) =>
        Array.from({ length: puzzle.size }, (_, col) => place(row, col))
    );

    return (
        <div className="cwRun">
            <div className="cwTop">
                <Plate />
                <div className="cwStatusBox">
                    <p className="cwStatus">
                        {format(t("flashCards.crosswordStatusTemplate"), {
                            n: book.caseNumber,
                            total: CASEBOOK_CASES,
                            glasses: book.glasses,
                            max: MAX_GLASSES,
                        })}
                    </p>
                    <p className="cwWorth">
                        <span className="cwLenses" aria-hidden="true">
                            {Array.from({ length: GLASSES_PER_CASE }, (_, i) => (
                                <Lens key={i} lost={i >= worth} />
                            ))}
                        </span>
                        <span>{format(t("flashCards.crosswordWorthTemplate"), { n: worth, max: GLASSES_PER_CASE })}</span>
                    </p>
                </div>
            </div>

            <section className="cwFile">
                <div className="cwHead">
                    <Silhouette />
                    <div>
                        <p className="cwCaseTitle">{text.caseTitle(textIndex)}</p>
                        <p className="cwBrief">{text.caseBrief(textIndex)}</p>
                    </div>
                </div>

                <div className={`cwBoard cwBoard--${puzzle.size}`} role="group" aria-label={t("flashCards.crosswordBoardLabel")}>
                    {rows}
                </div>

                <ul className="srOnly" aria-label={t("flashCards.crosswordLinesLabel")}>
                    {puzzle.lines.map((line) => (
                        <li key={line.id}>{text.lineReading(puzzle, line, current.entries)}</li>
                    ))}
                </ul>

                {rules.bank && (
                    <div className="cwBankBox" role="group" aria-label={t("flashCards.crosswordBankLabel")}>
                        <ul className="cwBank">
                            {bank.map((value, i) => (
                                <li key={i}>
                                    <button
                                        type="button"
                                        className={`cwChip${used[i] ? " cwChip--used" : ""}`}
                                        disabled={used[i] || solved}
                                        onClick={() => placeFromBank(value)}
                                    >
                                        {value}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <p className="cwMessage">{message}</p>

                <div className="cwActions">
                    {solved ? (
                        <button type="button" className="btn btn-primary cwNext" ref={nextRef} onClick={next}>
                            {lastCase ? t("flashCards.crosswordFinish") : t("flashCards.crosswordNextCase")}
                        </button>
                    ) : (
                        <>
                            <button type="button" className="btn btn-secondary cwAction" onClick={check}>
                                {t("flashCards.crosswordCheck")}
                            </button>
                            <button type="button" className="btn btn-secondary cwAction" onClick={ask}>
                                {t("flashCards.crosswordAsk")}
                            </button>
                        </>
                    )}
                </div>
            </section>

            <p className="srOnly" role="status" aria-live="polite">
                {announce}
            </p>
        </div>
    );
}

export default CrosswordGameRun;
